import { useCallback } from "react";
import { LaborSelection } from "./useLaborMasterDataInit";

interface UseLaborUpdateProps {
    setSelectedValues: React.Dispatch<React.SetStateAction<Record<string, LaborSelection>>>;
}

/**
 * useLaborUpdate
 * -----------------
 * Handles user interactions for Labor items.
 * Pure state updates only. Sync is managed separately via useLaborDatabaseSync.
 */
export function useLaborUpdate({ setSelectedValues }: UseLaborUpdateProps) {
    const apply = useCallback(
        (updater: (prev: Record<string, LaborSelection>) => Record<string, LaborSelection>) => {
            setSelectedValues((prev) => updater(prev));
        },
        [setSelectedValues],
    );

    // ─── ACTION 1: User modifies the quantity ───────────────────────────────
    const handleQuantityUpdate = useCallback(
        (laborServiceId: string, value: string | number) => {
            apply((prev) => {
                const currentQty = prev[laborServiceId]?.quantity || 0;
                let newQty = currentQty;

                if (value === "add") {
                    newQty = currentQty + 1;
                } else if (value === "remove") {
                    newQty = Math.max(0, currentQty - 1);
                } else if (typeof value === "number") {
                    newQty = Math.max(0, value);
                }

                return {
                    ...prev,
                    [laborServiceId]: {
                        ...prev[laborServiceId],
                        quantity: newQty,
                    },
                };
            });
        },
        [apply],
    );

    // ─── ACTION 2: User clicks the trash icon to remove a labor item ────────
    const handleClearLabor = useCallback(
        (laborServiceId: string) => {
            apply((prev) => {
                const next = { ...prev };
                delete next[laborServiceId];
                return next;
            });
        },
        [apply],
    );

    return { handleQuantityUpdate, handleClearLabor };
}
